import { createCometTheme } from "@comet/admin";
import { type CSSProperties } from "react";

declare module "@mui/material/styles" {
    interface Theme {
        appDrawer: {
            width: CSSProperties["width"];
        };
        colors: {
            [key_: string]: CSSProperties["color"];
        };
    }

    interface ThemeOptions {
        appDrawer?: {
            width?: CSSProperties["width"];
        };
        colors?: {
            [key_: string]: CSSProperties["color"];
        };
    }
}

// Override Comet's responsive typography at the md+ breakpoint where it scales sizes up.
const mdUp = "@media (min-width:900px)";
const fontFamily = '"Roboto", sans-serif';

const theme = createCometTheme({
    spacing: 5,
    typography: {
        fontFamily,
        // Comet v8 sets fontFamily on each variant, so we have to repeat it per variant
        // rather than relying on the top-level value.
        h1: {
            fontFamily,
            lineHeight: "44px",
            fontSize: "34px",
            fontWeight: 500,
            [mdUp]: {
                fontFamily,
                lineHeight: "44px",
                fontSize: "34px",
                fontWeight: 500,
            },
        },
        h5: {
            fontFamily,
            fontSize: "20px",
            lineHeight: "24px",
            fontWeight: 500,
            [mdUp]: {
                fontFamily,
                fontSize: "20px",
                lineHeight: "24px",
                fontWeight: 500,
            },
        },
        body1: {
            fontFamily,
            fontSize: "16px",
            lineHeight: "24px",
            fontWeight: 400,
        },
        body2: {
            fontFamily,
            fontSize: "12px",
            lineHeight: "16px",
            fontWeight: 400,
        },
    },
    palette: {
        primary: {
            main: "#1D6B4E",
            light: "#4F9A7A",
            dark: "#003F26",
            contrastText: "#FFFFFF",
        },
        secondary: {
            main: "#D64937",
        },
        text: {
            primary: "#000000",
            secondary: "#3C3C3C",
        },
        error: {
            main: "#cc3314",
        },
    },
    appDrawer: {
        width: 300,
    },
    colors: {
        borderGrey: "#B3B3B3",
        lightGrey: "rgba(0, 0, 0, 0.12)",
        inputBorder: "#e0e0e0",
        textGrey: "#4C4C4C",
        error: "rgba(230, 23, 23, 0.07)",
        invalid: "#F2F2F2",
        send: "rgba(1, 128, 255,0.07)",
    },
    components: {
        MuiToolbar: {
            defaultProps: {
                variant: "dense",
            },
        },
        MuiListItem: {
            defaultProps: {
                dense: true,
            },
        },
        MuiTable: {
            defaultProps: {
                size: "small",
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    backgroundColor: "#F5F5F5",
                },
                // Comet's TablePagination puts a `justifyContent: space-between` Grid container
                // inside a MUI Toolbar (a flex container). MUI v7's Grid no longer sets
                // `width: 100%` on containers the way v4's did, so the Grid shrink-wraps to its
                // content as a flex item, space-between gets no free space, and the whole footer
                // ("<n> Einträge", "Seite x von y", the arrows) bunches up on the left.
                footer: {
                    "& > .MuiToolbar-root > .MuiGrid-container": {
                        width: "100%",
                    },
                },
            },
        },
        // MasterMenu look: grey selected state, smaller text, no row dividers.
        // Comet's createComponentSlot maps each classesResolver token (level1/level2/...) to its
        // own styleOverrides key, so we target `level1` directly rather than nesting in `root`.
        CometAdminMainNavigationItem: {
            styleOverrides: {
                level1: {
                    height: 60,
                    paddingTop: "14px !important",
                    paddingBottom: "14px !important",
                    borderBottom: "none !important",
                    "& .MuiListItemText-primary": {
                        fontSize: "14px !important",
                        lineHeight: "20px !important",
                        fontWeight: "500 !important",
                    },
                    "& .MuiListItemIcon-root": {
                        "& .MuiSvgIcon-root, & svg": {
                            fontSize: "20px !important",
                            width: "20px !important",
                            height: "20px !important",
                        },
                    },
                    "&:hover": {
                        color: "#3C3C3C !important",
                        backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                        "& .MuiListItemIcon-root": {
                            color: "#3C3C3C !important",
                        },
                    },
                    "&.Mui-selected": {
                        color: "#3C3C3C !important",
                        backgroundColor: "rgba(0, 0, 0, 0.06) !important",
                        "& .MuiListItemIcon-root": {
                            color: "#3C3C3C !important",
                        },
                        "&:after": {
                            backgroundColor: "#B3B3B3 !important",
                        },
                        "&:hover": {
                            color: "#3C3C3C !important",
                            backgroundColor: "rgba(0, 0, 0, 0.1) !important",
                            "& .MuiListItemIcon-root": {
                                color: "#3C3C3C !important",
                            },
                        },
                    },
                },
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        // Remove Comet v8 border around the AppBar hamburger button to match prod.

        CometAdminAppHeaderMenuButton: {
            styleOverrides: {
                root: {
                    border: "none !important",
                    "& .MuiSvgIcon-root, & svg": {
                        fontSize: "24px !important",
                        width: "24px !important",
                        height: "24px !important",
                    },
                },
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
    },
});

export default theme;
